'use client'

import { Suspense, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'lib/i18n/routing'
import MarketoForm from 'components/marketo-form'
import type { MarketoFormAPIResponse } from 'components/marketo-form/types'
import { FlightIcon } from '@hashicorp/react-mds/src/components/flight-icon'
import sendPosthogEvent from '@web/utils/send-posthog-event'
import { trackEvent } from 'fathom-client'
import TwoColumnField from '../two-column-field'
import { track } from '@hashicorp/platform-analytics'
import type { ContactSalesPageQuery } from 'types/dato'
import s from './styles.module.css'

interface MarketoContactSalesProps {
	marketoForm: MarketoFormAPIResponse
}

/**
 * Determine the Marketo Field ID from the provided product slug
 */
function determineFieldId(
	productSlug: string
):
	| 'Vault__c'
	| 'Nomad__c'
	| 'Terraform__c'
	| 'Consul__c'
	| 'Packer__c'
	| 'Boundary__c' {
	switch (productSlug) {
		case 'vault':
			return 'Vault__c'
		case 'nomad':
			return 'Nomad__c'
		case 'terraform':
			return 'Terraform__c'
		case 'consul':
			return 'Consul__c'
		case 'packer':
			return 'Packer__c'
		case 'boundary':
			return 'Boundary__c'
	}

	throw new Error(`Unknown product field: ${productSlug}`)
}

// Determine initial values from query and URL parameters
function calculateInitialValues(
	interest: string | string[] | undefined
): Record<string, boolean> {
	const initialValues: Record<string, boolean> = {}

	// Determine interested products from query parameters
	// Ex. interest=consul, interest=consul,terraform
	if (interest) {
		const interestedProducts = Array.isArray(interest)
			? interest
			: interest.split(',')
		interestedProducts.forEach((product) => {
			try {
				const fieldId = determineFieldId(product)
				initialValues[fieldId] = true
			} catch (err) {
				console.error(err)
			}
		})
	}

	return initialValues
}

type MarketoContactSalesFormProps = MarketoContactSalesProps &
	ContactSalesPageQuery['contactSalesPage']

const MarketoContactSalesForm = ({
	marketoForm,
	heading,
	...rest
}: MarketoContactSalesFormProps) => {
	const [error, setError] = useState('')

	return (
		<>
			<h1 className={s.header}>{heading}</h1>
			<Suspense fallback={<div className={s.skeleton} />}>
				<Form {...{ marketoForm, setError, heading, ...rest }} />
			</Suspense>
			{error && (
				<div className={s.error}>
					<FlightIcon name="alert-triangle-fill" size={16} />
					{error}
				</div>
			)}
		</>
	)
}

const Form = ({
	marketoForm,
	setError,
	heading = 'Contact sales',
	errorText = 'There was an error submitting the form. Please check your information and try again.',
}: MarketoContactSalesFormProps & {
	setError: (error: string) => void
}) => {
	const { push } = useRouter()
	const searchParams = useSearchParams()
	const interest = searchParams.get('interest')

	return (
		<MarketoForm
			{...marketoForm}
			// Supply the default field groups (for first/last name) in addition
			// to the field config for product interest checkboxes.
			groups={{
				lineOne: {
					fields: ['FirstName', 'LastName'],
					component: TwoColumnField,
				},
				lineTwo: {
					fields: ['Email', 'Company'],
					component: TwoColumnField,
				},
				lineThree: {
					fields: ['Country', 'Phone'],
					component: TwoColumnField,
				},
			}}
			submitTitle={
				marketoForm.metadata.result[0].buttonLabel ?? 'Contact sales'
			}
			// Provide additional validation to handle validating multiple
			// product interest fields.
			validateFields={async (values, errors) => {
				const e = {
					...errors,
				}
				return { values, errors: e }
			}}
			// Pre-check product based on either URL param or query param.
			initialValues={calculateInitialValues(interest)}
			onSubmitStart={() => {
				setError('')
			}}
			onSubmitSuccess={(data) => {
				trackEvent('Primary sales form filled')
				track('Contact Sales Form Submitted')
				sendPosthogEvent('Contact Sales Form Submitted', {
					productInterest: interest ?? 'None',
				})
				push('/thank-you/contact-sales')
			}}
			onSubmitError={() => {
				setError(errorText)
			}}
			formName={heading}
		/>
	)
}

export default MarketoContactSalesForm
